import React, { useState, useEffect } from 'react';
import {
  Mail, Phone, Users, Briefcase, MapPin, User, Calendar, Clock,
  CheckCircle, BookOpen, Target, Download, X, Shield, Key, Settings, Info
} from 'lucide-react';
import useMyTeamStore from '@/stores/AdminStore/MyTeamStore';
import { TeamMember } from '@/services/PagesServices/MyTeamService';
import './MyTeam.css';
import TeamDashboardTab from './TeamDashboardTab';
import LearningHistoryTab from './LearningHistoryTab';
import AssignTrainingTab from './AssignTrainingTab';
import ManageTeamsTab from './ManageTeamsTab';

const MyTeam: React.FC = () => {
  const {
    teamMembers,
    courses,
    learningHistory,
    adminActivities,
    teamStats,
    loading,
    fetchTeamMembers,
    fetchCourses,
    fetchLearningHistory,
    fetchAdminActivities,
    fetchTeamStats,
    bulkEnroll,
  } = useMyTeamStore();

  const [activeTab, setActiveTab] = useState<'dashboard' | 'history' | 'assign' | 'manage'>('dashboard');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<string>('');
  const [sendNotification, setSendNotification] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<TeamMember | null>(null);
  const [userDetailTab, setUserDetailTab] = useState<'details' | 'history' | 'enrollment' | 'admin'>('details');
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState<{ enrolledCount: number; userCount: number; courseCount: number } | null>(null);

  // Lazy load data based on active tab
  useEffect(() => {
    if (activeTab === 'dashboard' && !teamMembers) {
      fetchTeamMembers();
    }
    if (activeTab === 'dashboard' && !teamStats) {
      fetchTeamStats();
    }
  }, [activeTab, teamMembers, teamStats, fetchTeamMembers, fetchTeamStats]);

  useEffect(() => {
    if (activeTab === 'history' && !learningHistory) {
      fetchLearningHistory();
    }
    if (activeTab === 'history' && !teamMembers) {
      fetchTeamMembers();
    }
  }, [activeTab, learningHistory, teamMembers, fetchLearningHistory, fetchTeamMembers]);

  useEffect(() => {
    if (activeTab === 'assign' && !teamMembers) {
      fetchTeamMembers();
    }
    if (activeTab === 'assign' && !courses) {
      fetchCourses();
    }
  }, [activeTab, teamMembers, courses, fetchTeamMembers, fetchCourses]);

  // Fetch data for user detail modal tabs
  useEffect(() => {
    if (selectedUser && userDetailTab === 'history' && !learningHistory) {
      fetchLearningHistory();
    }
    if (selectedUser && userDetailTab === 'enrollment' && !courses) {
      fetchCourses();
    }
    if (selectedUser && userDetailTab === 'admin' && !adminActivities) {
      fetchAdminActivities();
    }
  }, [selectedUser, userDetailTab, learningHistory, courses, adminActivities, fetchLearningHistory, fetchCourses, fetchAdminActivities]);

  const toggleMemberSelection = (id: string) => {
    setSelectedMembers(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const toggleCourseSelection = (id: string) => {
    setSelectedCourses(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleAssignTraining = async () => {
    const result = await bulkEnroll(selectedMembers, selectedCourses, dueDate || undefined);
    if (result.success && result.data) {
      setAssignmentResult(result.data);
      setShowAssignmentModal(true);
      // Clear selections
      setSelectedMembers([]);
      setSelectedCourses([]);
      setDueDate('');
      setSendNotification(false);
    }
  };

  const handleViewUser = (userId: string) => {
    const user = teamMembers?.find(m => m.id === userId);
    if (user) {
      setSelectedUser(user);
      setUserDetailTab('details');
    }
  };

  const handleCloseUserDetail = () => {
    setSelectedUser(null);
  };

  const handleEnrollCourse = (courseName: string) => {
    alert(`Enrolling ${selectedUser?.name} in ${courseName}`);
  };

  if (selectedUser) {
    return (
      <div className="admin-my-team-modal-overlay">
        <div className="admin-my-team-user-detail-modal">
          <div className="admin-my-team-user-detail-header">
            <div className="admin-my-team-user-detail-info">
              <div className="admin-my-team-avatar admin-my-team-avatar-large">{selectedUser.initials}</div>
              <div>
                <h2 className="admin-my-team-user-detail-name">{selectedUser.name}</h2>
                <p className="admin-my-team-user-detail-title">{selectedUser.jobTitle}</p>
              </div>
              <span className="admin-my-team-status-badge-active">Active</span>
            </div>
            <button className="admin-my-team-close-btn" onClick={handleCloseUserDetail}>
              <X size={20} />
            </button>
          </div>

          <div className="admin-my-team-user-detail-tabs">
            <button
              className={`admin-my-team-user-detail-tab ${userDetailTab === 'details' ? 'admin-my-team-user-detail-tab-active' : ''}`}
              onClick={() => setUserDetailTab('details')}
            >
              User Details
            </button>
            <button
              className={`admin-my-team-user-detail-tab ${userDetailTab === 'history' ? 'admin-my-team-user-detail-tab-active' : ''}`}
              onClick={() => setUserDetailTab('history')}
            >
              Learning History
            </button>
            <button
              className={`admin-my-team-user-detail-tab ${userDetailTab === 'enrollment' ? 'admin-my-team-user-detail-tab-active' : ''}`}
              onClick={() => setUserDetailTab('enrollment')}
            >
              Manual Enrollment
            </button>
            <button
              className={`admin-my-team-user-detail-tab ${userDetailTab === 'admin' ? 'admin-my-team-user-detail-tab-active' : ''}`}
              onClick={() => setUserDetailTab('admin')}
            >
              Admin Actions
            </button>
          </div>

          <div className="admin-my-team-user-detail-content">
            {userDetailTab === 'details' && (
              <div className="admin-my-team-user-details-tab">
                <section className="admin-my-team-detail-section">
                  <h3 className="admin-my-team-detail-section-title">Personal Information</h3>
                  <div className="admin-my-team-info-grid">
                    <div className="admin-my-team-info-item">
                      <div className="admin-my-team-info-label">EMAIL</div>
                      <div className="admin-my-team-info-value">
                        <Mail size={16} className="admin-my-team-info-icon" />
                        {selectedUser.email}
                      </div>
                    </div>
                    <div className="admin-my-team-info-item">
                      <div className="admin-my-team-info-label">PHONE</div>
                      <div className="admin-my-team-info-value">
                        <Phone size={16} className="admin-my-team-info-icon" />
                        {selectedUser.phone}
                      </div>
                    </div>
                    <div className="admin-my-team-info-item">
                      <div className="admin-my-team-info-label">DEPARTMENT</div>
                      <div className="admin-my-team-info-value">
                        <Users size={16} className="admin-my-team-info-icon" />
                        {selectedUser.department}
                      </div>
                    </div>
                    <div className="admin-my-team-info-item">
                      <div className="admin-my-team-info-label">JOB TITLE</div>
                      <div className="admin-my-team-info-value">
                        <Briefcase size={16} className="admin-my-team-info-icon" />
                        {selectedUser.jobTitle}
                      </div>
                    </div>
                    <div className="admin-my-team-info-item">
                      <div className="admin-my-team-info-label">LOCATION</div>
                      <div className="admin-my-team-info-value">
                        <MapPin size={16} className="admin-my-team-info-icon" />
                        {selectedUser.location}
                      </div>
                    </div>
                    <div className="admin-my-team-info-item">
                      <div className="admin-my-team-info-label">MANAGER</div>
                      <div className="admin-my-team-info-value">
                        <User size={16} className="admin-my-team-info-icon" />
                        {selectedUser.manager}
                      </div>
                    </div>
                    <div className="admin-my-team-info-item">
                      <div className="admin-my-team-info-label">JOIN DATE</div>
                      <div className="admin-my-team-info-value">
                        <Calendar size={16} className="admin-my-team-info-icon" />
                        {selectedUser.joinDate}
                      </div>
                    </div>
                    <div className="admin-my-team-info-item">
                      <div className="admin-my-team-info-label">LAST LOGIN</div>
                      <div className="admin-my-team-info-value">
                        <Clock size={16} className="admin-my-team-info-icon" />
                        {selectedUser.lastLogin}
                      </div>
                    </div>
                  </div>
                </section>

                <section className="admin-my-team-detail-section">
                  <h3 className="admin-my-team-detail-section-title">Learning Statistics</h3>
                  <div className="admin-my-team-stats-grid">
                    <div className="admin-my-team-stat-box">
                      <div className="admin-my-team-stat-box-label">Completed</div>
                      <div className="admin-my-team-stat-box-value">{selectedUser.completedCourses}</div>
                      <CheckCircle size={32} className="admin-my-team-stat-box-icon" />
                    </div>
                    <div className="admin-my-team-stat-box">
                      <div className="admin-my-team-stat-box-label">In Progress</div>
                      <div className="admin-my-team-stat-box-value">{selectedUser.inProgressCourses}</div>
                      <BookOpen size={32} className="admin-my-team-stat-box-icon" />
                    </div>
                    <div className="admin-my-team-stat-box">
                      <div className="admin-my-team-stat-box-label">Completion Rate</div>
                      <div className="admin-my-team-stat-box-value">{selectedUser.progress}%</div>
                      <Target size={32} className="admin-my-team-stat-box-icon" />
                    </div>
                  </div>
                </section>
              </div>
            )}

            {userDetailTab === 'history' && (
              <div className="admin-my-team-user-history-tab">
                <div className="admin-my-team-history-tab-header">
                  <h3 className="admin-my-team-detail-section-title">Learning History</h3>
                  <button className="admin-my-team-export-btn">
                    <Download size={16} />
                    Export Transcript
                  </button>
                </div>

                <div className="admin-my-team-table-container">
                  <table className="admin-my-team-table">
                    <thead>
                      <tr>
                        <th>COURSE</th>
                        <th>TYPE</th>
                        <th>STATUS</th>
                        <th>PROGRESS</th>
                        <th>SCORE</th>
                        <th>COMPLETED</th>
                      </tr>
                    </thead>
                    <tbody>
                      {learningHistory?.map(item => (
                        <tr key={item.id}>
                          <td>
                            <div className="admin-my-team-course-info">
                              <div className="admin-my-team-course-icon">
                                <BookOpen size={20} />
                              </div>
                              <span>{item.name}</span>
                            </div>
                          </td>
                          <td>{item.type}</td>
                          <td>
                            <span className={`admin-my-team-status-badge ${
                              item.status === 'Completed' ? 'admin-my-team-status-completed' : 'admin-my-team-status-progress'
                            }`}>
                              {item.status}
                            </span>
                          </td>
                          <td>
                            <div className="admin-my-team-progress-cell">
                              <span>{item.progress}%</span>
                              <div className="admin-my-team-progress-bar">
                                <div
                                  className="admin-my-team-progress-fill"
                                  style={{ width: `${item.progress}%` }}
                                />
                              </div>
                            </div>
                          </td>
                          <td>{item.score}</td>
                          <td>{item.completed}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {userDetailTab === 'enrollment' && (
              <div className="admin-my-team-enrollment-tab">
                <div className="admin-my-team-enrollment-header">
                  <h3 className="admin-my-team-detail-section-title">Manual Enrollment</h3>
                  <p className="admin-my-team-enrollment-subtitle">Enroll {selectedUser.name} in courses, paths, or certifications</p>
                </div>

                <input
                  type="text"
                  className="admin-my-team-search-input"
                  placeholder="Search learning items..."
                />

                <div className="admin-my-team-enrollment-list">
                  {courses?.map(course => (
                    <div key={course.id} className="admin-my-team-enrollment-item">
                      <div className="admin-my-team-enrollment-item-info">
                        <div className="admin-my-team-course-icon">
                          <BookOpen size={20} />
                        </div>
                        <div>
                          <div className="admin-my-team-enrollment-item-name">{course.name}</div>
                          <div className="admin-my-team-enrollment-item-meta">
                            Course • {course.duration} • {course.lessons} modules
                          </div>
                        </div>
                      </div>
                      <button
                        className="admin-my-team-enroll-btn"
                        onClick={() => handleEnrollCourse(course.name)}
                      >
                        + Enroll
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {userDetailTab === 'admin' && (
              <div className="admin-my-team-admin-tab">
                <div className="admin-my-team-admin-header">
                  <h3 className="admin-my-team-detail-section-title">Administrative Actions</h3>
                  <p className="admin-my-team-enrollment-subtitle">Perform administrative tasks for this user</p>
                </div>

                <div className="admin-my-team-current-status">
                  <div className="admin-my-team-status-label">CURRENT STATUS</div>
                  <div className="admin-my-team-status-info">
                    <div className="admin-my-team-status-icon">
                      <Shield size={24} />
                    </div>
                    <div>
                      <span className="admin-my-team-status-badge-active">Active</span>
                      <span className="admin-my-team-status-last-login"> • Last login: {selectedUser.lastLogin}</span>
                    </div>
                  </div>
                </div>

                <div className="admin-my-team-admin-actions-grid">
                  <div className="admin-my-team-admin-action-card">
                    <div className="admin-my-team-admin-action-icon admin-my-team-icon-blue">
                      <Shield size={28} />
                    </div>
                    <h4 className="admin-my-team-admin-action-title">Update Status</h4>
                    <p className="admin-my-team-admin-action-desc">Change account status</p>
                    <button className="admin-my-team-admin-action-btn admin-my-team-btn-blue">Change Status</button>
                  </div>

                  <div className="admin-my-team-admin-action-card">
                    <div className="admin-my-team-admin-action-icon admin-my-team-icon-purple">
                      <Key size={28} />
                    </div>
                    <h4 className="admin-my-team-admin-action-title">Reset Password</h4>
                    <p className="admin-my-team-admin-action-desc">Send reset link</p>
                    <button className="admin-my-team-admin-action-btn admin-my-team-btn-purple">Send Reset Link</button>
                  </div>

                  <div className="admin-my-team-admin-action-card">
                    <div className="admin-my-team-admin-action-icon admin-my-team-icon-yellow">
                      <Settings size={28} />
                    </div>
                    <h4 className="admin-my-team-admin-action-title">Change Role</h4>
                    <p className="admin-my-team-admin-action-desc">Update permissions</p>
                    <button className="admin-my-team-admin-action-btn admin-my-team-btn-yellow">Update Role</button>
                  </div>
                </div>

                <div className="admin-my-team-guidelines-box">
                  <div className="admin-my-team-guidelines-header">
                    <Info size={20} className="admin-my-team-guidelines-icon" />
                    <span className="admin-my-team-guidelines-title">Status Change Guidelines</span>
                  </div>
                  <div className="admin-my-team-guidelines-content">
                    <div className="admin-my-team-guideline-item">
                      <strong>Active:</strong> User has full access to the platform
                    </div>
                    <div className="admin-my-team-guideline-item">
                      <strong>Inactive:</strong> Temporary access suspension (can be reactivated)
                    </div>
                    <div className="admin-my-team-guideline-item">
                      <strong>Suspended:</strong> Account under review or policy violation
                    </div>
                    <div className="admin-my-team-guideline-item">
                      <strong>Terminated:</strong> Employment ended, permanent deactivation
                    </div>
                    <div className="admin-my-team-guideline-item">
                      <strong>Resigned:</strong> User voluntarily left the organization
                    </div>
                  </div>
                </div>

                <div className="admin-my-team-activity-section">
                  <h4 className="admin-my-team-activity-title">Recent Admin Activity</h4>
                  <div className="admin-my-team-activity-list">
                    {adminActivities?.map((activity, index) => (
                      <div key={index} className="admin-my-team-activity-item">
                        <div className="admin-my-team-activity-icon">
                          <Clock size={16} />
                        </div>
                        <div>
                          <div className="admin-my-team-activity-action">{activity.action}</div>
                          <div className="admin-my-team-activity-date">{activity.date}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-my-team-container">
      <div className="admin-my-team-breadcrumb">
        <span>User Management</span>
        <span className="admin-my-team-separator">›</span>
        <span>My Team</span>
      </div>

      <div className="admin-my-team-header">
        <h1>Team Management</h1>
        <p className="admin-my-team-subtitle">Manage users, teams, and learning assignments</p>
      </div>

      <div className="admin-my-team-tabs">
        <button
          className={`admin-my-team-tab ${activeTab === 'dashboard' ? 'admin-my-team-tab-active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Team Dashboard
        </button>
        <button
          className={`admin-my-team-tab ${activeTab === 'history' ? 'admin-my-team-tab-active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Learning History
        </button>
        <button
          className={`admin-my-team-tab ${activeTab === 'assign' ? 'admin-my-team-tab-active' : ''}`}
          onClick={() => setActiveTab('assign')}
        >
          Assign Training
        </button>
        <button
          className={`admin-my-team-tab ${activeTab === 'manage' ? 'admin-my-team-tab-active' : ''}`}
          onClick={() => setActiveTab('manage')}
        >
          Manage Teams
        </button>
      </div>

      {activeTab === 'dashboard' && (
        <TeamDashboardTab onViewUser={handleViewUser} />
      )}

      {activeTab === 'history' && (
        <LearningHistoryTab />
      )}

      {activeTab === 'assign' && (
        <AssignTrainingTab
          selectedMembers={selectedMembers}
          selectedCourses={selectedCourses}
          dueDate={dueDate}
          sendNotification={sendNotification}
          onToggleMemberSelection={toggleMemberSelection}
          onToggleCourseSelection={toggleCourseSelection}
          onSetDueDate={setDueDate}
          onSetSendNotification={setSendNotification}
          onAssignTraining={handleAssignTraining}
        />
      )}

      {activeTab === 'manage' && (
        <ManageTeamsTab />
      )}

      {/* Assignment Result Modal */}
      {showAssignmentModal && assignmentResult && (
        <div className="admin-my-team-modal-overlay" onClick={() => setShowAssignmentModal(false)}>
          <div className="admin-my-team-user-detail-modal" style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <div className="admin-my-team-user-detail-header">
              <div className="admin-my-team-user-detail-info">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#10b981',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff'
                  }}>
                    <CheckCircle size={28} />
                  </div>
                  <div>
                    <h2 className="admin-my-team-user-detail-name">Training Assigned Successfully!</h2>
                    <p className="admin-my-team-user-detail-title">Enrollments have been created</p>
                  </div>
                </div>
              </div>
              <button className="admin-my-team-close-btn" onClick={() => setShowAssignmentModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="admin-my-team-user-detail-content" style={{ padding: '24px' }}>
              <div className="admin-my-team-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="admin-my-team-stat-box">
                  <div className="admin-my-team-stat-box-label">Enrollments</div>
                  <div className="admin-my-team-stat-box-value">{assignmentResult.enrolledCount}</div>
                  <CheckCircle size={32} className="admin-my-team-stat-box-icon" />
                </div>
                <div className="admin-my-team-stat-box">
                  <div className="admin-my-team-stat-box-label">Users</div>
                  <div className="admin-my-team-stat-box-value">{assignmentResult.userCount}</div>
                  <Users size={32} className="admin-my-team-stat-box-icon" />
                </div>
                <div className="admin-my-team-stat-box">
                  <div className="admin-my-team-stat-box-label">Courses</div>
                  <div className="admin-my-team-stat-box-value">{assignmentResult.courseCount}</div>
                  <BookOpen size={32} className="admin-my-team-stat-box-icon" />
                </div>
              </div>

              <div style={{
                marginTop: '24px',
                padding: '16px',
                background: '#f0fdf4',
                borderRadius: '8px',
                border: '1px solid #86efac'
              }}>
                <p style={{ margin: 0, color: '#166534', fontSize: '14px' }}>
                  {assignmentResult.enrolledCount} new enrollment(s) have been created for {assignmentResult.userCount} user(s) across {assignmentResult.courseCount} course(s).
                  {sendNotification && ' Email notifications will be sent to all enrolled users.'}
                </p>
              </div>

              <div style={{ marginTop: '24px', textAlign: 'center' }}>
                <button
                  className="admin-my-team-assign-btn"
                  onClick={() => setShowAssignmentModal(false)}
                  style={{ padding: '12px 32px' }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTeam;
